import { useEffect, useRef, useState, useCallback } from 'react'

interface UseStickyHorizontalScrollbarOptions {
  /** Whether the sticky scrollbar is enabled */
  enabled?: boolean
  /** Offset from bottom of viewport (e.g., to account for floating action toolbar) */
  bottomOffset?: number
  /** The container element that holds the DataGrid */
  containerRef: React.RefObject<HTMLElement>
  /** Whether this component is currently active (useful for tab-based layouts) */
  isActive?: boolean
  /**
   * A value that changes whenever the underlying grid (re)appears — e.g. the row count.
   * When it changes, the setup effect re-runs so listeners/observers attach to a grid that
   * mounted after an async data load (when the grid started as an empty-state placeholder).
   */
  refreshKey?: unknown
}

interface UseStickyHorizontalScrollbarReturn {
  /** Ref to attach to the sticky scrollbar container */
  stickyScrollbarRef: React.RefObject<HTMLDivElement>
  /** Whether the sticky scrollbar should be visible */
  isVisible: boolean
  /** Width of the scrollable content */
  scrollWidth: number
  /** Left position to align with the table */
  leftPosition: number
  /** Width of the visible container */
  containerWidth: number
}

/**
 * Custom hook to create a sticky horizontal scrollbar that stays visible
 * at the bottom of the viewport while scrolling vertically.
 *
 * This is particularly useful for wide tables where the horizontal scrollbar
 * would otherwise be hidden when scrolled down.
 */
export const useStickyHorizontalScrollbar = ({
  enabled = true,
  bottomOffset = 0,
  containerRef,
  isActive = true,
  refreshKey,
}: UseStickyHorizontalScrollbarOptions): UseStickyHorizontalScrollbarReturn => {
  const stickyScrollbarRef = useRef<HTMLDivElement>(null)
  const [isVisible, setIsVisible] = useState(false)
  const [scrollWidth, setScrollWidth] = useState(0)
  const [leftPosition, setLeftPosition] = useState(0)
  const [containerWidth, setContainerWidth] = useState(0)
  const syncingFromSticky = useRef(false)
  const syncingFromGrid = useRef(false)
  const rafId = useRef<number | null>(null)

  // Find the actual scrollable element within the DataGrid
  const getGridScrollContainer = useCallback((): HTMLElement | null => {
    if (!containerRef.current) return null

    // MUI DataGrid's scrollable container has class 'MuiDataGrid-virtualScroller'
    const virtualScroller = containerRef.current.querySelector('.MuiDataGrid-virtualScroller') as HTMLElement
    return virtualScroller
  }, [containerRef])

  // Sync scroll position from sticky scrollbar to DataGrid
  const handleStickyScroll = useCallback((e: Event) => {
    if (syncingFromGrid.current) return

    const stickyScrollbar = e.target as HTMLElement
    const gridScroller = getGridScrollContainer()

    if (gridScroller && stickyScrollbar) {
      syncingFromSticky.current = true
      gridScroller.scrollLeft = stickyScrollbar.scrollLeft
      requestAnimationFrame(() => {
        syncingFromSticky.current = false
      })
    }
  }, [getGridScrollContainer])

  // Sync scroll position from DataGrid to sticky scrollbar
  const handleGridScroll = useCallback(() => {
    if (syncingFromSticky.current) return

    const gridScroller = getGridScrollContainer()
    const stickyScrollbar = stickyScrollbarRef.current

    if (gridScroller && stickyScrollbar) {
      syncingFromGrid.current = true
      stickyScrollbar.scrollLeft = gridScroller.scrollLeft
      requestAnimationFrame(() => {
        syncingFromGrid.current = false
      })
    }
  }, [getGridScrollContainer])

  // Update scroll width and position when content changes
  const updateScrollWidth = useCallback(() => {
    const gridScroller = getGridScrollContainer()
    if (gridScroller && containerRef.current) {
      setScrollWidth(gridScroller.scrollWidth)

      // Update position and width to align with table
      const containerRect = containerRef.current.getBoundingClientRect()
      setLeftPosition(containerRect.left)
      setContainerWidth(containerRect.width)
    }
  }, [getGridScrollContainer, containerRef])

  // Determine if sticky scrollbar should be visible
  const updateVisibility = useCallback(() => {
    if (!enabled || !isActive || !containerRef.current) {
      setIsVisible(false)
      return
    }

    const gridScroller = getGridScrollContainer()
    if (!gridScroller) {
      setIsVisible(false)
      return
    }

    // Check if content is wider than container (needs horizontal scroll)
    const needsHorizontalScroll = gridScroller.scrollWidth > gridScroller.clientWidth

    // The grid's own (native) horizontal scrollbar sits at the bottom edge of the scroller.
    // Show the sticky bar ONLY when that native scrollbar is off-screen (the grid extends
    // below the viewport). Once the grid bottom scrolls into view, the native scrollbar is
    // usable, so the sticky bar hides — the two never appear together.
    const rect = gridScroller.getBoundingClientRect()
    const viewportBottom = window.innerHeight - bottomOffset
    const gridOnScreen = rect.top < viewportBottom && rect.bottom > 0
    const nativeScrollbarVisible = rect.bottom <= viewportBottom

    const shouldBeVisible = needsHorizontalScroll && gridOnScreen && !nativeScrollbarVisible
    setIsVisible(shouldBeVisible)
  }, [enabled, isActive, containerRef, getGridScrollContainer, bottomOffset])

  // Coalesce re-measurements into a single requestAnimationFrame so we read layout
  // AFTER it settles, never mid-mutation. Reading synchronously inside the observers
  // can catch a transient state (e.g. scrollWidth === clientWidth while MUI recomputes
  // dimensions on pointer interaction), which would wrongly hide the bar until the next
  // mutation. Deferring to the next frame avoids that latching.
  const scheduleRecompute = useCallback(() => {
    if (rafId.current !== null) return
    rafId.current = requestAnimationFrame(() => {
      rafId.current = null
      updateScrollWidth()
      updateVisibility()
    })
  }, [updateScrollWidth, updateVisibility])

  // Setup scroll synchronization and visibility detection.
  // MUI builds the DataGrid's internal DOM (.MuiDataGrid-virtualScroller) in its own effects,
  // so it may not exist yet when this effect first runs (especially right after an async data
  // load). We therefore retry until both the grid scroller and the sticky bar are present, and
  // only THEN attach the listeners/observers. The previous one-shot retry recomputed visibility
  // but never attached listeners, leaving the bar visible-but-non-functional.
  useEffect(() => {
    if (!enabled || !isActive) return

    let cancelled = false
    let attempts = 0
    let retryTimeout: ReturnType<typeof setTimeout> | undefined
    let cleanupAttached: (() => void) | undefined

    const trySetup = () => {
      if (cancelled) return

      const gridScroller = getGridScrollContainer()
      const stickyScrollbar = stickyScrollbarRef.current

      if (!gridScroller || !stickyScrollbar) {
        if (attempts++ < 30) {
          retryTimeout = setTimeout(trySetup, 100) // ~3s window for MUI's DOM to appear
        }
        return
      }

      // Initial measurement
      updateScrollWidth()
      updateVisibility()

      // Scroll synchronization (kept synchronous so dragging never lags)
      stickyScrollbar.addEventListener('scroll', handleStickyScroll, { passive: true })
      gridScroller.addEventListener('scroll', handleGridScroll, { passive: true })

      // Visibility + width detection (rAF-coalesced)
      window.addEventListener('scroll', scheduleRecompute, { passive: true })
      window.addEventListener('resize', scheduleRecompute, { passive: true })

      const resizeObserver = new ResizeObserver(scheduleRecompute)
      resizeObserver.observe(gridScroller)

      const mutationObserver = new MutationObserver(scheduleRecompute)
      mutationObserver.observe(gridScroller, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style'],
      })

      cleanupAttached = () => {
        stickyScrollbar.removeEventListener('scroll', handleStickyScroll)
        gridScroller.removeEventListener('scroll', handleGridScroll)
        window.removeEventListener('scroll', scheduleRecompute)
        window.removeEventListener('resize', scheduleRecompute)
        resizeObserver.disconnect()
        mutationObserver.disconnect()
      }
    }

    trySetup()

    return () => {
      cancelled = true
      if (retryTimeout !== undefined) clearTimeout(retryTimeout)
      cleanupAttached?.()
      if (rafId.current !== null) {
        cancelAnimationFrame(rafId.current)
        rafId.current = null
      }
    }
  }, [enabled, isActive, refreshKey, handleStickyScroll, handleGridScroll, updateScrollWidth, updateVisibility, scheduleRecompute, getGridScrollContainer])

  return {
    stickyScrollbarRef,
    isVisible,
    scrollWidth,
    leftPosition,
    containerWidth,
  }
}

export default useStickyHorizontalScrollbar
